import { Loader } from 'components';
import { useAuth } from 'contexts';
import { useCustomerAPI } from 'hooks/use-customer-api';
import {
  createContext,
  Dispatch,
  FC,
  FormEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer, EdiCustomerLocation, EDIType, Role } from 'types';
import { formatCustomer } from 'utils/format';

type EditCustomerContextType = {
  recipients: Map<number, Partial<EdiCustomerLocation>>;
  setRecipients: Dispatch<SetStateAction<EditCustomerContextType['recipients']>>;
};

const EditCustomerContext = createContext<EditCustomerContextType>({ recipients: new Map(), setRecipients: () => {} });

const Recipient: FC<{ id: number; recipient: Partial<EdiCustomerLocation> }> = ({ id, recipient }) => {
  const { recipients, setRecipients } = useContext(EditCustomerContext);

  const handleChange = useCallback(
    (updates: Partial<EdiCustomerLocation>) => {
      setRecipients(prevState => new Map(prevState).set(id, { ...prevState.get(id), ...updates }));
    },
    [id, setRecipients]
  );

  const handleDelete = useCallback(() => {
    setRecipients(prevState => {
      const mapCopy = new Map(prevState);
      mapCopy.delete(id);
      return mapCopy;
    });
  }, [id, setRecipients]);

  return !recipient ? null : (
    <div className="grid-4 p-2">
      <div>
        <h6 className="text-info-dark">EDI Type *</h6>
        <div className="mt-3 w-100">
          <select
            className="w-100"
            onChange={e => handleChange({ ediType: e.target.value as 'GATE' | 'LIFT' | 'STATUS' })}
            value={`${recipient.ediType ?? ''}`}
            required
          >
            <option key="" value="" hidden />
            {Object.values(EDIType).map(text => (
              <option key={text} value={text}>
                {text}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Mapping ID *</h6>
        <div className="mt-2 w-100">
          <input
            type="text"
            className="mt-2 w-100"
            onChange={e => handleChange({ mappingId: e.target.value })}
            value={recipient.mappingId ?? ''}
            required
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Partnership ID *</h6>
        <div className="mt-2 w-100">
          <input
            type="text"
            className="mt-2 w-100"
            onChange={e => handleChange({ partnershipId: e.target.value })}
            value={recipient.partnershipId ?? ''}
            required
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Customer Code</h6>
        <div className="mt-2 w-100">
          <input
            type="text"
            className="mt-2 w-100"
            onChange={e => handleChange({ customerCode: e.target.value })}
            value={recipient.customerCode ?? ''}
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Location Code</h6>
        <div className="mt-2 w-100">
          <input
            type="text"
            className="mt-2 w-100"
            onChange={e => handleChange({ locationCode: e.target.value })}
            value={recipient.locationCode ?? ''}
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">File Extension *</h6>
        <div className="mt-2 w-100">
          <input
            type="text"
            className="mt-2 w-100"
            onChange={e => handleChange({ fileExtension: e.target.value })}
            value={recipient.fileExtension ?? ''}
            required
          />
        </div>
      </div>
      <div>
        <h6 className="text-info-dark">Enabled</h6>
        <div className="mt-2">
          <input
            type="checkbox"
            className="mt-2"
            defaultChecked={recipient.enabled}
            onChange={e => handleChange({ enabled: e.target.checked })}
          />
        </div>
      </div>
      <div className="flex-fill d-flex align-items-end h-100">
        <button className="btn-back p-2" type="button" onClick={handleDelete} disabled={recipients.size === 0}>
          <span className="material-icons icon-xlarge">delete</span>
        </button>
      </div>
    </div>
  );
};

const EditEdiRecipients: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();

  const { getCustomerById, updateEdiRecipients } = useCustomerAPI();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [recipients, setRecipients] = useState<Map<number, Partial<EdiCustomerLocation>>>(new Map());

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const customerId = Number(params.id);
    if (isNaN(customerId)) return;
    getCustomerById(customerId).then(response => {
      setCustomer(response);
      if (!response?.ediRecipients?.length) return;
      setRecipients(response.ediRecipients.reduce((acc, val) => (!val?.id ? acc : acc.set(val.id, val)), new Map()));
    });
  }, [getCustomerById, params.id]);

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    navigate(!params.id ? '/settings/customers' : `/settings/customers/${params.id}`, { replace: true });
  }, [navigate, params.id]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      if (!customer?.id) return;
      setIsSubmitting(true);
      const response = await updateEdiRecipients(customer.id, [...recipients.values()] as EdiCustomerLocation[]);
      setIsSubmitting(false);
      if (response) navigate(`/settings/customers/${response.id}`, { replace: true });
    },
    [customer?.id, navigate, recipients, updateEdiRecipients]
  );

  return (
    <EditCustomerContext.Provider value={{ recipients, setRecipients }}>
      <Loader loading={!customer}>
        <form className="page" id="edit-customer" onReset={handleReset} onSubmit={handleSubmit}>
          <div className="page-header">
            <h1>{formatCustomer(customer)}</h1>
            <div className="d-flex g-3 flex-row-reverse">
              <Loader loading={isSubmitting}>
                <button className="btn-primary" type="submit">
                  SUBMIT
                </button>
              </Loader>
              <button className="btn-outline" type="reset">
                CANCEL
              </button>
            </div>
          </div>
          <div className="page-content flex-fill">
            {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && (
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">EDI Recipient(s)</h4>
                </div>
                <div className="card-body">
                  <div className="p-2">
                    {[...recipients.entries()].map(([key, val]) => (
                      <Recipient key={key} id={key} recipient={val} />
                    ))}
                    <div className="p-2">
                      <button
                        className="btn-outline"
                        type="button"
                        onClick={() => setRecipients(prevState => new Map(prevState).set(-new Date().valueOf(), {}))}
                      >
                        Add Recipient
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
      </Loader>
    </EditCustomerContext.Provider>
  );
};

export default EditEdiRecipients;
